// src/components/Header.jsx
import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext.jsx";
import { useTranslation } from "react-i18next";
import { doc, getDoc } from "firebase/firestore";

import { db } from "../firebase/firebase.jsx";
import {
    createBusinessForOwner,
    getMyBusinesses,
} from "../firebase/businessService.js";
import { setAppLanguage } from "../i18n.jsx";

export default function Header() {
    const auth = useAuth() || {};
    const { user, isAdmin, isOwner, approved, signOut, refreshClaims } = auth;
    const { t, i18n } = useTranslation();
    const location = useLocation();
    const navigate = useNavigate();

    const isLoggedIn = !!user;

    const [menuOpen, setMenuOpen] = useState(false);
    const [mobileNavOpen, setMobileNavOpen] = useState(false);

    const menuRef = useRef(null);
    const buttonRef = useRef(null);
    const mobileNavRef = useRef(null);
    const mobileButtonRef = useRef(null);

    const [businessAccessUnlocked, setBusinessAccessUnlocked] = useState(false);
    const [businessSlots, setBusinessSlots] = useState(0);
    const [loadingBusinessAccess, setLoadingBusinessAccess] = useState(false);

    const currentLanguage = useMemo(() => {
        return String(i18n.resolvedLanguage || i18n.language || "en").split("-")[0];
    }, [i18n.resolvedLanguage, i18n.language]);

    const navClass = ({ isActive }) =>
        "header-link" + (isActive ? " header-link-active" : "");

    const displayLabel = useMemo(
        () => user?.displayName || user?.email || t("account.label", "Account"),
        [user, t]
    );

    const shortLabel = useMemo(() => {
        const s = String(displayLabel || "");
        return s.length > 20 ? s.slice(0, 20) + "…" : s;
    }, [displayLabel]);

    const closeMenu = () => setMenuOpen(false);
    const closeMobileNav = () => setMobileNavOpen(false);

    const toggleMenu = () => {
        if (!isLoggedIn) return;
        setMenuOpen((prev) => !prev);
        setMobileNavOpen(false);
    };

    const toggleMobileNav = () => {
        setMobileNavOpen((prev) => !prev);
        setMenuOpen(false);
    };

    const handleSignOut = () => {
        closeMenu();
        closeMobileNav();
        if (signOut) signOut();
    };

    const handleLanguageChange = async (e) => {
        const lang = e.target.value;
        await setAppLanguage(lang);
    };

    const isMaintenanceModeError = (e) =>
        e?.code === "maintenance-mode" || e?.message === "maintenance-mode";

    const loadBusinessAccess = useCallback(async () => {
        if (!user?.uid) {
            setBusinessAccessUnlocked(false);
            setBusinessSlots(0);
            return {
                businessAccessUnlocked: false,
                businessSlots: 0,
                hasBusinessAccess: false,
            };
        }

        setLoadingBusinessAccess(true);

        try {
            const snap = await getDoc(doc(db, "users", user.uid));
            const data = snap.exists() ? snap.data() || {} : {};

            const unlocked = !!data.businessAccessUnlocked || !!data.hasBusinessAccess;
            const slots = Number(data.businessSlots || 0);
            const hasBusinessAccess = unlocked || !!isOwner || !!isAdmin;

            setBusinessAccessUnlocked(unlocked);
            setBusinessSlots(slots);

            return {
                businessAccessUnlocked: unlocked,
                businessSlots: slots,
                hasBusinessAccess,
            };
        } catch (err) {
            console.warn("[Header] Failed to load business access:", err);
            return {
                businessAccessUnlocked: false,
                businessSlots: 0,
                hasBusinessAccess: !!isOwner || !!isAdmin,
            };
        } finally {
            setLoadingBusinessAccess(false);
        }
    }, [user?.uid, isOwner, isAdmin]);

    const hasBusinessAccess = businessAccessUnlocked || !!isOwner || !!isAdmin;

    const hideHeaderForUnapprovedAccount =
        isLoggedIn && !approved && !hasBusinessAccess && !isOwner && !isAdmin;

    const effectiveBusinessSlots = useMemo(() => {
        const raw = Number(businessSlots || 0);
        if (hasBusinessAccess && raw < 1) return 1;
        return raw;
    }, [businessSlots, hasBusinessAccess]);

    const goMyBusiness = useCallback(async () => {
        closeMenu();
        closeMobileNav();

        if (!isLoggedIn) {
            navigate("/auth");
            return;
        }

        try {
            if (typeof refreshClaims === "function") {
                try {
                    await refreshClaims(user);
                } catch (err) {
                    console.warn("[Header] refreshClaims failed:", err);
                }
            }

            let access = {
                hasBusinessAccess,
                businessAccessUnlocked,
                businessSlots,
            };

            if (!access.hasBusinessAccess) {
                try {
                    access = await loadBusinessAccess();
                } catch (err) {
                    console.warn("[Header] Lazy load access failed:", err);
                }
            }

            const canAccessBusiness =
                access?.hasBusinessAccess ||
                businessAccessUnlocked ||
                isOwner ||
                isAdmin;

            if (!canAccessBusiness) {
                navigate("/payment?mode=unlock");
                return;
            }

            try {
                const mine = await getMyBusinesses();

                if (!mine || mine.length === 0) {
                    await createBusinessForOwner();
                }
            } catch (e) {
                if (!isMaintenanceModeError(e)) {
                    console.warn("[Header] Business bootstrap skipped:", e);
                }
            }

            navigate("/dashboard");
        } catch (e) {
            console.error("[Header] goMyBusiness failed:", e);

            if (businessAccessUnlocked || isOwner || isAdmin) {
                navigate("/dashboard");
                return;
            }

            navigate("/payment?mode=unlock");
        }
    }, [
        isLoggedIn,
        navigate,
        refreshClaims,
        user,
        loadBusinessAccess,
        businessAccessUnlocked,
        businessSlots,
        hasBusinessAccess,
        isOwner,
        isAdmin,
    ]);

    useEffect(() => {
        if (!menuOpen) return;

        const handleClick = (e) => {
            if (!menuRef.current) return;
            if (!menuRef.current.contains(e.target)) closeMenu();
        };

        document.addEventListener("mousedown", handleClick);
        return () => document.removeEventListener("mousedown", handleClick);
    }, [menuOpen]);

    useEffect(() => {
        if (!mobileNavOpen) return;

        const handleClick = (e) => {
            if (!mobileNavRef.current) return;
            if (!mobileNavRef.current.contains(e.target)) closeMobileNav();
        };

        document.addEventListener("mousedown", handleClick);
        return () => document.removeEventListener("mousedown", handleClick);
    }, [mobileNavOpen]);

    useEffect(() => {
        if (!menuOpen && !mobileNavOpen) return;

        const onKeyDown = (e) => {
            if (e.key === "Escape" || e.key === "Esc") {
                e.preventDefault();
                closeMenu();
                closeMobileNav();
                buttonRef.current?.focus();
                mobileButtonRef.current?.focus();
            }
        };

        window.addEventListener("keydown", onKeyDown, true);
        return () => window.removeEventListener("keydown", onKeyDown, true);
    }, [menuOpen, mobileNavOpen]);

    useEffect(() => {
        closeMenu();
        closeMobileNav();
    }, [location.pathname]);

    useEffect(() => {
        loadBusinessAccess();
    }, [loadBusinessAccess]);

    if (location.pathname === "/auth") return null;
    if (hideHeaderForUnapprovedAccount) return null;

    const showBusinessButton = isLoggedIn && hasBusinessAccess;

    const renderMainNavLinks = (mobile = false) => (
        <>
            <NavLink
                to="/search"
                end
                className={mobile ? "mobile-nav-item" : navClass}
                onClick={mobile ? closeMobileNav : undefined}
            >
                🔍 {t("nav.search", "Business search")}
            </NavLink>

            <NavLink
                to="/favorites"
                className={mobile ? "mobile-nav-item" : navClass}
                onClick={mobile ? closeMobileNav : undefined}
            >
                ⭐ {t("nav.favorites", "Favorites")}
            </NavLink>

            {showBusinessButton && (
                <button
                    type="button"
                    className={mobile ? "mobile-nav-item mobile-nav-button" : "header-link"}
                    onClick={goMyBusiness}
                    disabled={loadingBusinessAccess}
                >
                    🏷️ {t("nav.ownerDashboard", "My business")}
                </button>
            )}

            <NavLink
                to="/payment"
                className={mobile ? "mobile-nav-item" : navClass}
                onClick={mobile ? closeMobileNav : undefined}
            >
                💳 {t("nav.billing", "Billing")}
            </NavLink>

            {isAdmin && (
                <NavLink
                    to="/admin"
                    className={mobile ? "mobile-nav-item" : navClass}
                    onClick={mobile ? closeMobileNav : undefined}
                >
                    🛠️ {t("nav.admin", "Admin")}
                </NavLink>
            )}
        </>
    );

    return (
        <header className="app-header" role="banner">
            <div className="app-header-inner">
                <button
                    ref={mobileButtonRef}
                    type="button"
                    className="mobile-menu-toggle"
                    onClick={toggleMobileNav}
                    aria-label={t("nav.mobileMenu", "Open navigation menu")}
                    aria-expanded={mobileNavOpen ? "true" : "false"}
                >
                    ☰
                </button>

                <NavLink to="/search" className="mobile-brand" onClick={closeMobileNav}>
                    🌐 BUZHERE
                </NavLink>

                <nav className="header-nav" aria-label={t("nav.primary", "Primary navigation")}>
                    {renderMainNavLinks(false)}
                </nav>

                <div ref={menuRef} className="header-right">
                    <select
                        value={currentLanguage}
                        onChange={handleLanguageChange}
                        aria-label={t("account.preferredLanguage", "Preferred language")}
                        title={t("account.preferredLanguage", "Preferred language")}
                        className="header-language-select"
                    >
                        <option value="en">{t("languages.en")}</option>
                        <option value="es">{t("languages.es")}</option>
                    </select>

                    {isLoggedIn ? (
                        <>
                            <button
                                ref={buttonRef}
                                type="button"
                                onClick={toggleMenu}
                                className="header-account-btn header-account-desktop"
                                aria-haspopup="menu"
                                aria-expanded={menuOpen ? "true" : "false"}
                                aria-label={t("account.menuButton", "Open account menu")}
                            >
                                <span className="header-avatar" aria-hidden="true">
                                    {(displayLabel || "?").charAt(0).toUpperCase()}
                                </span>
                                <span className="header-account-text">{shortLabel}</span>
                                <span aria-hidden="true">▾</span>
                            </button>

                            {menuOpen && (
                                <div
                                    role="menu"
                                    aria-label={t("account.menu", "Account menu")}
                                    tabIndex={-1}
                                    className="header-menu"
                                >
                                    <div className="header-menu-top">
                                        {t("account.signedInAs", "Signed in as")}
                                        <div className="header-menu-identity">{displayLabel}</div>
                                    </div>

                                    <NavLink to="/account" role="menuitem" onClick={closeMenu} className="header-menu-item">
                                        ⚙️ {t("account.settings", "Account settings")}
                                    </NavLink>

                                    <NavLink to="/favorites" role="menuitem" onClick={closeMenu} className="header-menu-item">
                                        ⭐ {t("nav.favorites", "Favorites")}
                                    </NavLink>

                                    <NavLink to="/payment" role="menuitem" onClick={closeMenu} className="header-menu-item">
                                        💳 {t("nav.billing", "Billing")}
                                    </NavLink>

                                    {hasBusinessAccess && (
                                        <>
                                            <div className="header-menu-divider" />

                                            <button
                                                type="button"
                                                role="menuitem"
                                                onClick={goMyBusiness}
                                                className="header-menu-item"
                                                style={{ width: "100%", textAlign: "left" }}
                                            >
                                                🏷️ {t("business.manage", "Manage my business")}
                                            </button>

                                            <div className="px-3 py-2 text-xs text-slate-500">
                                                {t("account.owner.businessSlots", "Business slots")}: {effectiveBusinessSlots}
                                            </div>
                                        </>
                                    )}

                                    {isAdmin && (
                                        <>
                                            <div className="header-menu-divider" />
                                            <NavLink to="/admin" role="menuitem" onClick={closeMenu} className="header-menu-item">
                                                🛠️ {t("nav.adminPanel", "Admin panel")}
                                            </NavLink>
                                        </>
                                    )}

                                    <button
                                        type="button"
                                        onClick={handleSignOut}
                                        role="menuitem"
                                        className="header-menu-item header-logout"
                                    >
                                        🔓 {t("auth.logout", "Log out")}
                                    </button>
                                </div>
                            )}
                        </>
                    ) : (
                        <NavLink to="/auth" className="header-login-btn">
                            🔐 {t("auth.loginRegister", "Login / Register")}
                        </NavLink>
                    )}
                </div>
            </div>

            {mobileNavOpen && (
                <div className="mobile-nav-backdrop">
                    <div
                        ref={mobileNavRef}
                        className="mobile-nav-panel"
                        role="dialog"
                        aria-modal="true"
                        aria-label={t("nav.mobileMenu", "Navigation menu")}
                    >
                        <div className="mobile-nav-header">
                            <strong>🌐 BUZHERE</strong>

                            <button
                                type="button"
                                className="mobile-nav-close"
                                onClick={closeMobileNav}
                                aria-label={t("common.close", "Close")}
                            >
                                ×
                            </button>
                        </div>

                        {isLoggedIn && (
                            <div className="mobile-nav-account">
                                <div className="mobile-nav-account-avatar" aria-hidden="true">
                                    {(displayLabel || "?").charAt(0).toUpperCase()}
                                </div>

                                <div className="mobile-nav-account-info">
                                    <div className="mobile-nav-account-name">{displayLabel}</div>
                                    <div className="mobile-nav-account-subtitle">
                                        {t("account.signedInAs", "Signed in")}
                                    </div>
                                </div>
                            </div>
                        )}

                        <div className="mobile-nav-list">
                            {isLoggedIn && (
                                <NavLink
                                    to="/account"
                                    className="mobile-nav-item"
                                    onClick={closeMobileNav}
                                >
                                    ⚙️ {t("account.settings", "Account settings")}
                                </NavLink>
                            )}

                            {renderMainNavLinks(true)}
                        </div>

                        {isLoggedIn && hasBusinessAccess && (
                            <div className="mobile-nav-slots">
                                {t("account.owner.businessSlots", "Business slots")}: {effectiveBusinessSlots}
                            </div>
                        )}

                        {isLoggedIn && (
                            <>
                                <div className="mobile-nav-divider" />

                                <button
                                    type="button"
                                    onClick={handleSignOut}
                                    className="mobile-nav-item mobile-nav-button mobile-nav-logout"
                                >
                                    🔓 {t("auth.logout", "Log out")}
                                </button>
                            </>
                        )}
                    </div>
                </div>
            )}
        </header>
    );
}